// The collections a document trigger can watch, and the shape a document in each one has.
//
// Free of Angular and service imports, same rule as org-trigger-schedule.ts and
// org-trigger-template.ts: the spec loads this directly and must not drag in the DI chain.
//
// A PICKER rather than a free-text box: the collection name has to match Firestore exactly, and a
// typo produces a trigger that is valid, saves cleanly and never fires - with nothing on screen to
// say why. The names come from FirebaseConstants, which is where the rest of the app reads them.
//
// Curated, not the whole of FirebaseConstants: that file also holds field names (`kundennummer`)
// and infrastructure collections (`errors`, `system_config`, `branding`) which nobody wants an agent
// woken up by.

export interface WatchableCollection {
  value: string;
  label: string;
  // Field name -> what to call it on screen, keyed by the DOCUMENT-RELATIVE path ('vorname',
  // 'Ausbildung[].Beruf', and the bare group name 'Ausbildung' for its heading).
  //
  // The chips insert the real path either way; only the label changes. Without this they read
  // `NameOfPosition`, `DauerInMonaten`, `relatedEntityId` - database keys, half German and half
  // English, which is not a menu anyone can choose from. Anything unmapped falls back to the raw
  // path, so a field added to a sample still appears rather than vanishing.
  labels?: Record<string, string>;
  // The TOKEN a person drops into the message -> the document-relative path it stands for.
  //
  // `{{email}}`, not `{{after.Berufserfahrung[].NameOfPosition}}`. The message is something the user
  // reads back afterwards, so a data path in it is the schema leaking into their sentence. The path
  // is restored on save and the token restored on load - see expandTokens / collapseTokens.
  //
  // ASCII, lowercase, underscored, and UNIQUE across the whole collection: `DauerInMonaten` exists
  // under both Ausbildung and Berufserfahrung, so they cannot both be `{{dauer}}`.
  tokens?: Record<string, string>;
  // The document shape, used to build the {{...}} pills. Null where the shape has not been
  // confirmed against real data - see the note below.
  sample: any;
}

// One real applicant, trimmed to the fields worth writing a message against. Corrected against a
// 100-document dump: the array shapes here were previously inferred from applicant-information's
// template and had Ausbildung and Berufserfahrung the wrong way round - Ausbildung is the
// apprenticeship (Beruf/Betrieb/Dauer), Berufserfahrung the employment history. Inferring a shape
// from the screen that renders it is exactly the guess a chip must never be.
//
// Every field below is present in 100/100 documents. What is left out, and why:
//   - the *_lower search duplicates (city_lower, email_lower, firstName_lower, ...)
//   - firstname/firstName/lastname/lastName/fullName - the app writes vorname/nachname too
//   - city / country / postalCode and `residence` - a second and third copy of Wohnort
//   - copiedAt/copiedFromPath/copiedFromProject, importBatch, sdhRealImport/sdhTestImport,
//     formatted/formattedAt - import and migration bookkeeping
//   - senderemail / subject / textPlain / toAddress - the email the application arrived in,
//     not the applicant
//   - cv / extractedText - the full CV text, which would swamp the list
//   - cv_embedding / summary_embedding - 2304-float vectors, meaningless in a message
//   - education / workExperience / languageSkills - empty in all 100 documents, and English
//     duplicates of the German arrays that are populated
//   - the ~3% Talention integration fields (talention*, candidate_id, applicant[], findJob, ...)
//   - "akademischer Abschluss" - populated (62%) but the KEY CONTAINS A SPACE, so no placeholder
//     path can address it. Worth fixing in the data; unusable as a chip until then.
const APPLICANT_SAMPLE = {
  applicantId: 'SDH-100042',
  vorname: 'Lena',
  nachname: 'Weber',
  email: 'lena.weber@example.de',
  telefonnummer: '+49 341 2233445',
  mobilnummer: '+49 151 23456789',
  nationality: 'deutsch',
  jobTitle: 'Elektronikerin für Betriebstechnik',
  jobOfferId: 'JO-2026-0187',
  quelle: 'E-Mail Bewerbung',
  createdGerman: '13.08.2026, 09:12',
  Wohnort: {
    strasse: 'Karl-Liebknecht-Straße',
    hausnummer: '42',
    postleitzahl: '04107',
    city: 'Leipzig',
    country: 'Deutschland',
    languages: ['Deutsch', 'Englisch']
  },
  // Apprenticeship: what they trained as, where, and for how long.
  Ausbildung: [
    { Beruf: 'Elektronikerin für Betriebstechnik', Betrieb: 'Siemens AG', DauerInMonaten: 42 }
  ],
  // Employment history.
  Berufserfahrung: [
    {
      NameOfPosition: 'Servicetechnikerin',
      NameOfCompany: 'Stadtwerke Leipzig',
      Beschreibung: 'Wartung und Inbetriebnahme von Schaltanlagen',
      DauerInMonaten: 36,
      startDate: '2022-03',
      endDate: '2025-03'
    }
  ],
  Abschluss: [
    { NameOfAbschluss: 'Gesellenbrief Elektronikerin', Einrichtung: 'BSZ Leipzig', Datum: '2019-07' }
  ],
  // The job matches computed for this applicant.
  Ergebnis: [
    { Job: 'Elektroniker Betriebstechnik', Match: 87, Keywords: ['SPS', 'Wartung'] }
  ],
  attachments: [
    {
      name: 'Lebenslauf_Weber.pdf',
      originalFileName: 'lebenslauf.pdf',
      mimeType: 'application/pdf',
      downloadURL: 'https://storage.googleapis.com/…/lebenslauf.pdf',
      createdISO: '2026-08-13T07:12:00.000Z'
    }
  ],
  // Present on every document but EMPTY in all 100 sampled, so its element shape is unknown. Kept as
  // a bare array: "did skills change" is a real filter, and inventing an element would be a guess.
  skills: []
};

// One day's todo list for one user - the shape /hybrid-dashboard's todo widget reads, and where a
// message sent to a HUMAN node lands (see the chat dock's note on dailyTodos).
//
// Modelled on the fields that are ALWAYS there (107/107 todos) plus `metadata`, which is on 92% of
// them and describes the tool call a todo came from. Included as a map rather than null, because a
// null produces no field paths and offering them is the point.
//
// `contextSummary` is on 29% of documents. Kept anyway: it is the one thing that makes a daily
// briefing writable ("you have N emails, M job offers"), and a chip that is sometimes absent is
// still worth having when the alternative is not being able to say it at all.
//
// DELIBERATELY ABSENT - a second todo shape exists in 6 of 107 todos (5.6%), German-named:
// beschreibung, kategorie, rang, faelligkeit, datum_quelle, verknuepfte_entitaet, and the dueDate /
// proposedAction that travel with them. They duplicate text / category / priority under other names.
// Offering both spellings would put two chips on screen for the same value where one of them is
// empty 94% of the time, which is the exact noise the applicant sample was trimmed to avoid.
//
// Also absent: `contributesToGoal` and `targetPerson`, whose only observed type is null - a chip
// built from them could never resolve to anything. And `version`, which is bookkeeping.
const DAILY_TODO_SAMPLE = {
  uid: 'u_3wwYkqIo2cZS',
  date: '2026-08-13',
  generatedAt: '2026-08-13T05:00:00.000Z',
  contextSummary: {
    applicantCount: 12,
    appointmentCount: 2,
    calendarEventCount: 3,
    emailCount: 7,
    existingTaskCount: 5,
    jobOfferCount: 4,
    matchingCount: 9
  },
  todos: [
    {
      id: 'todo_8f21',
      text: 'Lena Weber zum Gespräch einladen',
      category: 'outreach',
      priority: 1,
      done: false,
      relatedEntityType: 'applicant',
      relatedEntityId: 'SDH-100042',
      metadata: {
        id: 'call_1f7a',
        name: 'send_email',
        text: 'E-Mail an Lena Weber senden',
        arguments: {
          subject: 'Einladung zum Gespräch',
          text: 'Guten Tag Frau Weber, wir würden Sie gerne kennenlernen.',
          htmlbody: '<p>Guten Tag Frau Weber,</p>',
          to_emails: ['lena.weber@example.de']
        }
      }
    }
  ]
};

// One agent's knowledge files, at users/{userId}/agentDocuments/{agentId}. The document id IS the
// agent id, and these two fields are the whole document - confirmed twice over: the 23-document dump,
// and the only writer, uploadContextCache (customerdatabase.service.ts), which sets exactly these.
//
// THE FIRST SUBCOLLECTION in this list, so its `value` is a path with a wildcard rather than a bare
// name - see the entry itself. That changes only WHICH documents are watched; everything here stays
// document-relative, because a path does not describe what is inside one.
//
// `files` is on 22 of 23. The one without it is a record whose last file was removed - deleteFileCache
// arrayRemoves and leaves the document behind - not a second shape.
//
// Populated rather than empty, unlike `skills` on the applicant: an empty array yields only the bare
// path `files` and no element chip, and the element - one file's gs:// URI - is what a message is
// actually written about. The element type is known here, so there is nothing to guess.
//
// `updated` is on all 23. The app writes `new Date()`, so it reads back as a Timestamp; the ISO string
// is for the pill builder, which cares about the shape and not the type.
const AGENT_DOCUMENT_SAMPLE = {
  files: ['gs://…/agentDocuments/agent_7f21/Mitarbeiterhandbuch.pdf'],
  updated: '2026-08-17T09:12:00.000Z'
};

// `sample: null` is deliberate and is not a gap to be filled in with a guess. A pill exists to stop
// someone retyping a field name wrongly; one built from an invented shape does exactly the thing it
// was added to prevent, except now the app vouches for it. These collections are pickable, and their
// message is written by hand until a real document confirms the shape.
// ONE entry, deliberately. The others (joboffers, teammembers, messages, vorlagen, activities) were
// listed with `sample: null` - pickable, but with no confirmed shape, so they produced no field chips
// and nothing could be checked against them. That advertises a collection the app cannot actually
// help you write a message for.
//
// Adding one back is two lines and one prerequisite: a real field dump from that collection, turned
// into a sample the way APPLICANT_SAMPLE was. Without the dump the chips would be guesses, which is
// the one thing a chip must never be - see the note above the samples.
// Wording, not structure - safe to change without touching the sample.
//
// ENGLISH, like every other string in this wizard. Translation belongs to i18n
// (TranslationService); hardcoding German here would pre-empt it and make this the one screen
// carrying two languages. When the trigger UI is internationalised these become keys.
const APPLICANT_LABELS: Record<string, string> = {
  applicantId: 'Applicant number',
  vorname: 'First name',
  nachname: 'Last name',
  email: 'E-mail',
  telefonnummer: 'Phone',
  mobilnummer: 'Mobile',
  nationality: 'Nationality',
  jobTitle: 'Job title',
  jobOfferId: 'Job offer ID',
  quelle: 'Source',
  createdGerman: 'Received',
  skills: 'Skills',
  Wohnort: 'Address',
  'Wohnort.strasse': 'Street',
  'Wohnort.hausnummer': 'House number',
  'Wohnort.postleitzahl': 'Postcode',
  'Wohnort.city': 'City',
  'Wohnort.country': 'Country',
  // Both spellings: the `[]` one labels an ELEMENT (the chips address one language), the bare one
  // labels the ARRAY, which is what changeableFieldsFor emits - it truncates at an array, so a field
  // picker built from it asks for 'Wohnort.languages' and showed the raw path without this.
  'Wohnort.languages': 'Languages',
  'Wohnort.languages[]': 'Languages',
  Ausbildung: 'Training',
  'Ausbildung[].Beruf': 'Role',
  'Ausbildung[].Betrieb': 'Company',
  'Ausbildung[].DauerInMonaten': 'Months',
  Berufserfahrung: 'Experience',
  'Berufserfahrung[].NameOfPosition': 'Position',
  'Berufserfahrung[].NameOfCompany': 'Company',
  'Berufserfahrung[].Beschreibung': 'Summary',
  'Berufserfahrung[].DauerInMonaten': 'Months',
  'Berufserfahrung[].startDate': 'From',
  'Berufserfahrung[].endDate': 'To',
  Abschluss: 'Qualification',
  'Abschluss[].NameOfAbschluss': 'Qualification',
  'Abschluss[].Einrichtung': 'Institution',
  'Abschluss[].Datum': 'Date',
  Ergebnis: 'Job matches',
  'Ergebnis[].Job': 'Job',
  'Ergebnis[].Match': 'Score',
  'Ergebnis[].Keywords[]': 'Keywords',
  attachments: 'Attachments',
  'attachments[].name': 'Name',
  'attachments[].originalFileName': 'Original name',
  'attachments[].mimeType': 'Type',
  'attachments[].downloadURL': 'Link',
  'attachments[].createdISO': 'Uploaded'
};

const DAILY_TODO_LABELS: Record<string, string> = {
  uid: 'User',
  date: 'Date',
  generatedAt: 'Generated',
  contextSummary: 'Today',
  'contextSummary.applicantCount': 'Applicants',
  'contextSummary.appointmentCount': 'Appointments',
  'contextSummary.calendarEventCount': 'Calendar events',
  'contextSummary.emailCount': 'E-mails',
  'contextSummary.existingTaskCount': 'Open tasks',
  'contextSummary.jobOfferCount': 'Job offers',
  'contextSummary.matchingCount': 'Matches',
  todos: 'Tasks',
  'todos[].id': 'Task ID',
  'todos[].text': 'Task',
  'todos[].category': 'Category',
  'todos[].priority': 'Priority',
  'todos[].done': 'Done',
  'todos[].relatedEntityType': 'Linked to (type)',
  'todos[].relatedEntityId': 'Linked to (ID)',
  'todos[].metadata.id': 'Tool call',
  'todos[].metadata.name': 'Tool',
  'todos[].metadata.text': 'Tool text',
  'todos[].metadata.arguments.subject': 'Subject',
  'todos[].metadata.arguments.text': 'Body',
  'todos[].metadata.arguments.htmlbody': 'HTML body',
  'todos[].metadata.arguments.to_emails[]': 'Recipients'
};

// Two fields, so this is all of them. `files[]` is the ELEMENT - the path a chip actually produces -
// and the bare `files` is the array, which is what the changed-field filter narrows on.
const AGENT_DOCUMENT_LABELS: Record<string, string> = {
  files: 'Knowledge files',
  'files[]': 'File',
  updated: 'Last changed'
};

// token -> document-relative path. English for the same reason the labels are.
const APPLICANT_TOKENS: Record<string, string> = {
  applicant_id: 'applicantId',
  first_name: 'vorname',
  last_name: 'nachname',
  email: 'email',
  phone: 'telefonnummer',
  mobile: 'mobilnummer',
  nationality: 'nationality',
  job_title: 'jobTitle',
  job_offer_id: 'jobOfferId',
  source: 'quelle',
  received_at: 'createdGerman',
  skills: 'skills',
  street: 'Wohnort.strasse',
  house_number: 'Wohnort.hausnummer',
  postcode: 'Wohnort.postleitzahl',
  city: 'Wohnort.city',
  country: 'Wohnort.country',
  languages: 'Wohnort.languages[]',
  training_role: 'Ausbildung[].Beruf',
  training_company: 'Ausbildung[].Betrieb',
  training_months: 'Ausbildung[].DauerInMonaten',
  position: 'Berufserfahrung[].NameOfPosition',
  company: 'Berufserfahrung[].NameOfCompany',
  role_summary: 'Berufserfahrung[].Beschreibung',
  job_months: 'Berufserfahrung[].DauerInMonaten',
  job_from: 'Berufserfahrung[].startDate',
  job_to: 'Berufserfahrung[].endDate',
  qualification: 'Abschluss[].NameOfAbschluss',
  qualification_school: 'Abschluss[].Einrichtung',
  qualification_date: 'Abschluss[].Datum',
  match_job: 'Ergebnis[].Job',
  match_score: 'Ergebnis[].Match',
  match_keywords: 'Ergebnis[].Keywords[]',
  file_name: 'attachments[].name',
  file_original_name: 'attachments[].originalFileName',
  file_type: 'attachments[].mimeType',
  file_link: 'attachments[].downloadURL',
  file_uploaded_at: 'attachments[].createdISO'
};

const DAILY_TODO_TOKENS: Record<string, string> = {
  user: 'uid',
  date: 'date',
  generated_at: 'generatedAt',
  applicant_count: 'contextSummary.applicantCount',
  appointment_count: 'contextSummary.appointmentCount',
  calendar_event_count: 'contextSummary.calendarEventCount',
  email_count: 'contextSummary.emailCount',
  open_task_count: 'contextSummary.existingTaskCount',
  job_offer_count: 'contextSummary.jobOfferCount',
  match_count: 'contextSummary.matchingCount',
  task: 'todos[].text',
  task_id: 'todos[].id',
  task_category: 'todos[].category',
  task_priority: 'todos[].priority',
  task_done: 'todos[].done',
  task_linked_type: 'todos[].relatedEntityType',
  task_linked_id: 'todos[].relatedEntityId',
  tool: 'todos[].metadata.name',
  tool_text: 'todos[].metadata.text',
  mail_subject: 'todos[].metadata.arguments.subject',
  mail_body: 'todos[].metadata.arguments.text',
  mail_recipients: 'todos[].metadata.arguments.to_emails[]'
};

// `file` is SINGULAR: a chip inserts one element, and the sentence it lands in is "a new file was
// added". No token for the bare `files` array - no chip produces that path, and an unknown {{...}} is
// left alone by expandTokens, so a hand-written {{after.files}} still survives the round trip.
const AGENT_DOCUMENT_TOKENS: Record<string, string> = {
  file: 'files[]',
  updated_at: 'updated'
};

export const WATCHABLE_COLLECTIONS: WatchableCollection[] = [
  { value: 'applicants', label: 'Bewerber (applicants)', sample: APPLICANT_SAMPLE, labels: APPLICANT_LABELS, tokens: APPLICANT_TOKENS },
  { value: 'dailyTodos', label: 'Tagesaufgaben (dailyTodos)', sample: DAILY_TODO_SAMPLE, labels: DAILY_TODO_LABELS, tokens: DAILY_TODO_TOKENS },
  // A SUBCOLLECTION, so the value is the full path pattern rather than a collection id: there is no
  // top-level `agentDocuments`, and the bare name would address one under every user document without
  // saying so. `{userId}` is a wildcard the backend expands, which means this watches EVERY user's
  // knowledge files and not the signed-in one's - the reach is deliberate, but it is reach. Confirmed
  // with Christian, 2026-08-17; the two entries above are top-level and take a bare name.
  { value: 'users/{userId}/agentDocuments', label: 'Agentenwissen (agentDocuments)', sample: AGENT_DOCUMENT_SAMPLE, labels: AGENT_DOCUMENT_LABELS, tokens: AGENT_DOCUMENT_TOKENS }
];

export function sampleForCollection(name: string): any {
  return WATCHABLE_COLLECTIONS.find(c => c.value === name)?.sample ?? null;
}

// The field names a document trigger can narrow on: `when: { changed: [...] }`.
//
// DOCUMENT-RELATIVE and unprefixed - `email`, not `after.email` and not `data.email`. This is not a
// placeholder: a placeholder is read out of the template context, where `before` and `after` are
// siblings holding the document, while this names a field INSIDE the document for the backend to
// compare across the two. The two look alike and mean different things, which is exactly why they
// are built by different functions.
//
// NESTED paths are included - `Wohnort.city` is as real a field as `email`, and the backend compares
// dotted paths (confirmed with Christian, 2026-08-11). Do not narrow this back to top-level keys
// without checking that again: it was top-level once, on a guess, and the only thing that made it
// look reasonable was that nobody had asked the backend.
//
// What IS excluded is anything under an ARRAY: `Ergebnis[].Job` addresses array contents by index,
// not by name, so it is not a field path and could never match. The arrays themselves stay - "did
// `skills` change" is a real filter. That is the only structural rule; depth is not one.
export function changeableFieldsFor(collection: string): string[] {
  return dottedPaths(sampleForCollection(collection), '');
}

function dottedPaths(value: any, prefix: string, depth = 0): string[] {
  if (depth > 4 || value === null || value === undefined) { return prefix ? [prefix] : []; }
  // An array's contents are addressed by index, not by name, so nothing below it is a field path.
  if (Array.isArray(value)) { return prefix ? [prefix] : []; }
  if (typeof value !== 'object') { return prefix ? [prefix] : []; }
  const out: string[] = [];
  for (const key of Object.keys(value)) {
    out.push(...dottedPaths(value[key], prefix ? `${prefix}.${key}` : key, depth + 1));
  }
  return out;
}

// What to call a field on screen. `path` is document-relative ('Ausbildung[].Beruf'), and the raw
// path is returned when nothing is mapped - a new field shows its key rather than disappearing.
export function fieldLabel(collection: string, path: string): string {
  const map = WATCHABLE_COLLECTIONS.find(c => c.value === collection)?.labels;
  return (map && map[path]) || path;
}

// The token standing for a path, or '' when the collection does not name one. Reverse of tokensFor.
export function tokenForPath(collection: string, path: string): string {
  const map = WATCHABLE_COLLECTIONS.find(c => c.value === collection)?.tokens || {};
  for (const token of Object.keys(map)) { if (map[token] === path) { return token; } }
  return '';
}

export function tokensFor(collection: string): Record<string, string> {
  return WATCHABLE_COLLECTIONS.find(c => c.value === collection)?.tokens || {};
}

// --- Token <-> path, the pair that must be exactly reversible -----------------------------------
//
// The user types `{{email}}`; the stored payload carries `{{after.email}}`. expandTokens runs on
// save, collapseTokens on load, and a message that goes through both has to come back identical -
// otherwise reopening a trigger silently rewrites what somebody wrote.
//
// `before` is spelled with a prefix rather than a mode, so ONE message can use both halves:
// `{{previous_email}}` and `{{email}}` side by side. Modes cannot express that.
const BEFORE_PREFIX = 'previous_';

// Anything that is not a known token is left ALONE - a user writing {{foo}} gets {{foo}} back, not a
// mangled path or a dropped brace. Same for a path already written out by hand.
export function expandTokens(message: string, collection: string): string {
  const map = tokensFor(collection);
  return (message || '').replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, token: string) => {
    const before = token.startsWith(BEFORE_PREFIX);
    const bare = before ? token.slice(BEFORE_PREFIX.length) : token;
    const path = map[bare];
    return path ? `{{${before ? 'before' : 'after'}.${path}}}` : whole;
  });
}

export function collapseTokens(message: string, collection: string): string {
  const map = tokensFor(collection);
  return (message || '').replace(/\{\{\s*(after|before)\.([^}]+?)\s*\}\}/g, (whole, root: string, path: string) => {
    for (const token of Object.keys(map)) {
      if (map[token] === path) { return `{{${root === 'before' ? BEFORE_PREFIX : ''}${token}}}`; }
    }
    return whole;   // a path with no token keeps its rooted form rather than vanishing
  });
}
